/* eslint-disable @typescript-eslint/no-empty-function */
import type { ContactFormValues } from '../types'
import type { RequestTourValues } from 'containers/public-listing/request-tour/types'
import type { FormikProps } from 'formik'
import type { Dispatch, SetStateAction } from 'react'

import { Box } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import { Link } from '@rentspree/component-2023.components.atoms.link'
import Select from '@rentspree/component-2023.components.atoms.select'
import TextInput from '@rentspree/component-2023.components.atoms.text-input'
import Typography from '@rentspree/component-2023.components.atoms.typography'
import NumberInput from '@rentspree/component-2023.components.organisms.number-input'
import Turnstile from 'react-turnstile'

import { DatePickerInput } from 'components/common/molecules/date-picker-input'
import {
  PRESCREEN_FIELDS,
  PRESCREEN_LEASE_TERM_OPTIONS,
  PRIVACY_POLICY_URL,
  TERMS_OF_USE_URL,
} from 'constant'
import env from 'env'

import styles from '../index.module.scss'

export type PrescreenFieldKeys = keyof typeof PRESCREEN_FIELDS
export type PrescreenFields = Partial<Record<PrescreenFieldKeys, string>>
export type PrescreenFormProps = {
  formik: FormikProps<ContactFormValues | RequestTourValues>
  setIsTurnstileLoading?: Dispatch<SetStateAction<boolean>>
}

// TODO: Move this component to be shared
export const PrescreenForm: React.FC<PrescreenFormProps> = ({
  formik,
  setIsTurnstileLoading = () => {},
}) => {
  const { values, handleChange, handleBlur, touched, errors, setFieldValue, isSubmitting } = formik
  const isTurnstileEnabled = env?.TURNSTILE_ENABLED
  return (
    <Box data-testid="prescreen-form" gap={2} mb={2} className={styles.prescreenFormContainer}>
      <Typography variant="title">Optional info</Typography>
      <DatePickerInput
        name="moveInDate"
        label="Desired move-In date"
        onDateChange={(value) => void setFieldValue('moveInDate', value)}
        value={values?.moveInDate}
        error={!!touched?.moveInDate && !!errors?.moveInDate}
        helperText={touched?.moveInDate ? errors.moveInDate : ''}
      />
      <Select
        id="preferredLeaseTerm"
        name="preferredLeaseTerm"
        label={PRESCREEN_FIELDS.preferredLeaseTerm.label}
        variant="outlined"
        placeholder={PRESCREEN_FIELDS.preferredLeaseTerm.placeholder}
        onChange={(e) => {
          void setFieldValue('preferredLeaseTerm', e.target.value)
        }}
        value={values?.preferredLeaseTerm}
        error={!!touched?.preferredLeaseTerm && !!errors?.preferredLeaseTerm}
        disabled={isSubmitting}
        helperText={touched?.preferredLeaseTerm ? errors.preferredLeaseTerm : ''}
        MenuProps={{
          style: { zIndex: 5050 },
        }}
      >
        {PRESCREEN_LEASE_TERM_OPTIONS.map(({ value: menuItemValue, label }) => (
          <MenuItem value={menuItemValue} key={menuItemValue}>
            <Typography variant="body-medium">{label}</Typography>
          </MenuItem>
        ))}
      </Select>
      <TextInput
        type="number"
        id="householdMonthlyIncome"
        name="householdMonthlyIncome"
        label="Household monthly income"
        onChange={handleChange}
        onBlur={handleBlur}
        startAdornment="$"
        value={values?.householdMonthlyIncome}
        error={!!touched?.householdMonthlyIncome && !!errors?.householdMonthlyIncome}
        helperText={touched?.householdMonthlyIncome ? errors.householdMonthlyIncome : ''}
      />
      <Box mb="16px">
        <Box className={styles.occupyContainer}>
          <Typography variant="body-medium">Total occupants</Typography>
          <NumberInput
            onChange={(e) => {
              void setFieldValue('occupy', e.target.valueAsNumber)
            }}
            value={parseInt(values.occupy, 10)}
            min={1}
            max={100}
          />
        </Box>
        <Box pl="12px">
          <Typography color="#222222BD" variant="caption">
            Include everyone who will live in the unit.
          </Typography>
        </Box>
      </Box>
      <Typography variant="body" color="#222222BD">
        We’ll send your message to the property contact. By continuing, you agree to receive
        communications from RentSpree and property contacts, following our{' '}
        <Link
          variant="bodySmall"
          href={TERMS_OF_USE_URL}
          target="_blank"
          className={styles.termOfUseLink}
        >
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link
          variant="bodySmall"
          href={PRIVACY_POLICY_URL}
          target="_blank"
          className={styles.termOfUseLink}
        >
          Privacy Policy
        </Link>
        .
      </Typography>
      {isTurnstileEnabled && (
        <Turnstile
          theme="light"
          sitekey={env?.TURNSTILE_SITE_KEY}
          action="rentspree-public-message"
          size="flexible"
          refreshExpired="auto"
          onLoad={() => setIsTurnstileLoading(true)}
          onVerify={() => setIsTurnstileLoading(false)}
          onSuccess={() => setIsTurnstileLoading(false)}
        />
      )}
    </Box>
  )
}
